import type { NotificationView } from '@/lib/api/generated/model';

/** One inbox row: the notifications about one task, or a lone workspace event. */
export interface InboxRow {
  key: string;
  /** The newest notification; the row says what it says. */
  latest: NotificationView;
  /** Every notification in the row, newest first. */
  items: NotificationView[];
  unread: number;
}

/**
 * Collapses a newest-first page into rows, one per task (spec 131 FR-004):
 * "3 updates on Hello world in Go" instead of three lines. A notification
 * with no task (a workspace event) is its own row. Rows keep the order of
 * their newest notification.
 */
export function groupByTask(
  notifications: readonly NotificationView[],
): InboxRow[] {
  const rows = new Map<string, InboxRow>();
  for (const notification of notifications) {
    const key = notification.task_id
      ? `task:${notification.task_id}`
      : `one:${notification.id}`;
    const row = rows.get(key);
    const unread = notification.read_at === null ? 1 : 0;
    if (row) {
      row.items.push(notification);
      row.unread += unread;
    } else {
      rows.set(key, {
        key,
        latest: notification,
        items: [notification],
        unread,
      });
    }
  }
  return [...rows.values()];
}
